import { useCallback, useEffect, useRef } from "react";
import type { AppDispatch } from "@/app/store";
import { handlePatientTableBroadcast } from "@redux/patientTableRecordsSlice";
import { handleActivePatientBroadcast } from "@features/patient-view/state/redux/activePatientDataSlice";
import { BroadcastCoalescer, createBroadcastCoalescer } from "@common/lib/broadcastCoalescer";

/** Trailing window that collapses a burst of "patient" broadcasts for one record into one refresh. */
export const PATIENT_BROADCAST_DEBOUNCE_MS = 750;
/** Upper bound on that delay while broadcasts keep arriving, so a busy record still refreshes. */
export const PATIENT_BROADCAST_MAX_WAIT_MS = 2500;

const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

/**
 * EN-1325: realtime "patient" broadcast → refresh, coalesced per record.
 *
 * Every open tab receives every broadcast. Refreshing immediately in each of them was the largest
 * web-app load on the Supabase primary. Both refreshes are deferred while the tab is hidden (a
 * hidden tab can't show either result, and the open-patient refresh is the full fetchPatientRecord
 * embed) and caught up once per record when it becomes visible. In a visible tab the open visit
 * still refreshes live, within the coalescing window.
 */
export function usePatientBroadcastRefresh(dispatch: AppDispatch): (patientRecordId: string) => void {
    const table = useRef<BroadcastCoalescer | null>(null);
    const active = useRef<BroadcastCoalescer | null>(null);

    useEffect(() => {
        table.current = createBroadcastCoalescer({
            delayMs: PATIENT_BROADCAST_DEBOUNCE_MS,
            maxWaitMs: PATIENT_BROADCAST_MAX_WAIT_MS,
            isHidden,
            run: (patientRecordId) => Promise.resolve(dispatch(handlePatientTableBroadcast({ patientRecordId }))),
        });
        active.current = createBroadcastCoalescer({
            delayMs: PATIENT_BROADCAST_DEBOUNCE_MS,
            maxWaitMs: PATIENT_BROADCAST_MAX_WAIT_MS,
            isHidden,
            run: (patientRecordId) => Promise.resolve(dispatch(handleActivePatientBroadcast({ patientRecordId }))),
        });

        const onVisibilityChange = () => {
            if (isHidden()) return;
            table.current?.flushDirty();
            active.current?.flushDirty();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
            table.current?.dispose();
            active.current?.dispose();
            table.current = null;
            active.current = null;
        };
    }, [dispatch]);

    return useCallback((patientRecordId: string) => {
        table.current?.schedule(patientRecordId);
        active.current?.schedule(patientRecordId);
    }, []);
}
